import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FaCheck, FaCopy } from 'react-icons/fa';

const CodeBlock = ({ language, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="code-block-wrapper">
      <div className="code-block-header">
        <span className="code-language">{language || 'plaintext'}</span>
        <button className="copy-code-button" onClick={handleCopy}>
          {copied ? (
            <>
              <FaCheck /> Copied!
            </>
          ) : (
            <>
              <FaCopy /> Copy code
            </>
          )}
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'plaintext'}
        style={vscDarkPlus}
        customStyle={{
          margin: 0,
          borderRadius: '0 0 8px 8px',
          fontSize: '0.9em',
        }}
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
};

const MessageRenderer = ({ content, isUser }) => {
  // Handle array content (multimodal messages)
  if (Array.isArray(content)) {
    const imageElements = [];
    const textElements = [];

    content.forEach((part, idx) => {
      if (part.type === 'image_url') {
        imageElements.push(
          <img
            key={idx}
            src={part.image_url?.url || part.image_url}
            alt="uploaded"
            className="message-image"
          />
        );
      } else if (part.type === 'video_url') {
        console.log("🎥 RENDERING VIDEO:", part.video_url?.url);
        imageElements.push(
          <video
            key={idx}
            src={part.video_url?.url || part.video_url}
            controls
            style={{ maxWidth: '100%', borderRadius: '8px', marginTop: '10px' }}
            onClick={() => window.open(part.video_url?.url || part.video_url, '_blank')}
          >
            Your browser does not support the video tag.
          </video>
        );
      } else if (part.type === 'text' || part.type === 'input_text' || part.type === 'output_text') {
        const text = part.text || part.input_text || part.output_text || '';
        textElements.push(
          <div key={idx} className="message-text">
            {isUser ? (
              // User messages: preserve formatting with pre-wrap
              <div className="user-text-content">{text}</div>
            ) : (
              // AI messages: render markdown with syntax highlighting
              <ReactMarkdown
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    const codeString = String(children).replace(/\n$/, '');

                    return !inline && match ? (
                      <CodeBlock language={match[1]} value={codeString} />
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {text}
              </ReactMarkdown>
            )}
          </div>
        );
      }
    });

    return (
      <div className="message-container">
        {imageElements.length > 0 && (
          <div className="image-thumbnails">{imageElements}</div>
        )}
        {textElements}
      </div>
    );
  }

  // Handle animation content
  if (content && content.type === 'animation') {
    return (
      <div className="message-container loading_animation">
        {/* Animation component will be passed from parent */}
        {content.component}
      </div>
    );
  }

  // Handle string content
  if (typeof content === 'string') {
    return (
      <div className="message-container">
        {isUser ? (
          // User messages: preserve formatting
          <div className="user-text-content">{content}</div>
        ) : (
          // AI messages: render markdown
          <ReactMarkdown
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                const codeString = String(children).replace(/\n$/, '');

                return !inline && match ? (
                  <CodeBlock language={match[1]} value={codeString} />
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {content}
          </ReactMarkdown>
        )}
      </div>
    );
  }

  return <div className="message-container">{JSON.stringify(content)}</div>;
};

export default MessageRenderer;
